/**
 * Training profile AI import utilities.
 * Normalizes whole-profile JSON authoring payloads, validates tutorial content,
 * converts AI-friendly configuration draft data into backend payloads, and
 * assembles the reference package used by the profile import modal.
 *
 * @module trainingProfileAiImportUtils
 */

import {
  TRAINING_PROFILE_CONFIGURATION_GROUPS,
  buildTrainingProfileFixedValues,
  buildTrainingProfileSimulationDefaults,
  buildTrainingProfileUiConfiguration,
  createTrainingProfileConfigurationDraft,
} from './trainingProfileConfiguration';
import { AVAILABLE_QUEST_ICONS } from '../Simulations/HeatPumpSimulation/questIcons';
import { AVAILABLE_SPOTLIGHT_SELECTORS } from '../Simulations/HeatPumpSimulation/spotlightTargets';

export const DEFAULT_CONDITION = Object.freeze({
  combinator: 'and',
  rules: [
    {
      field: 'outdoorTemperature',
      operator: 'lessThanOrEqual',
      value: -10,
      label: 'Outdoor temperature <= -10C',
    },
  ],
});

export const CROSS_FIELD_CONDITION = Object.freeze({
  combinator: 'and',
  rules: [
    {
      field: 'supplyTemperature',
      operator: 'greaterThanOrEqualFieldMultiplier',
      compareField: 'outdoorTemperature',
      multiplier: -2,
      label: 'Supply temperature is at least 2x the absolute outdoor temperature',
    },
  ],
});

export const TWO_CONDITION_EXAMPLE = Object.freeze({
  combinator: 'and',
  rules: [
    {
      field: 'outdoorTemperature',
      operator: 'lessThanOrEqual',
      value: -5,
      label: 'Outdoor temperature <= -5C',
    },
    {
      field: 'qNet',
      operator: 'greaterThanOrEqual',
      value: 0,
      label: 'Q-net is non-negative',
    },
  ],
});

export const SPOTLIGHT_POSITION_DEFINITIONS = Object.freeze([
  { key: 'top', description: 'Places the spotlight callout above the matched element.' },
  { key: 'bottom', description: 'Places the spotlight callout below the matched element.' },
  { key: 'left', description: 'Places the spotlight callout to the left of the matched element.' },
  { key: 'right', description: 'Places the spotlight callout to the right of the matched element.' },
]);

export const CONDITION_COMBINATOR_DEFINITIONS = Object.freeze([
  { key: 'and', description: 'Every child rule in the group must pass.' },
  { key: 'or', description: 'At least one child rule in the group must pass.' },
]);

export const CONDITION_OPERATOR_DEFINITIONS = Object.freeze([
  { key: 'equals', example: '{ "value": 5 }' },
  { key: 'notEquals', example: '{ "value": "living" }' },
  { key: 'greaterThan', example: '{ "value": 45 }' },
  { key: 'greaterThanOrEqual', example: '{ "value": 45 }' },
  { key: 'lessThan', example: '{ "value": 10 }' },
  { key: 'lessThanOrEqual', example: '{ "value": -10 }' },
  { key: 'between', example: '{ "value": [35, 45] }' },
  { key: 'in', example: '{ "value": ["living", "bathroom"] }' },
  { key: 'contains', example: '{ "value": "room" }' },
  { key: 'approximately', example: '{ "value": 50, "tolerance": 0.5 }' },
  { key: 'greaterThanOrEqualFieldMultiplier', example: '{ "compareField": "outdoorTemperature", "multiplier": -2 }' },
  { key: 'lessThanOrEqualFieldMultiplier', example: '{ "compareField": "heatOutput", "multiplier": 1.1 }' },
]);

export const CONDITION_FIELD_DEFINITIONS = Object.freeze([
  { key: 'isSimulationRunning', label: 'Simulation running', type: 'boolean' },
  { key: 'outdoorTemperature', label: 'Outdoor temperature', type: 'number' },
  { key: 'selectedRoomType', label: 'Selected room type', type: 'string' },
  { key: 'desiredTemperature', label: 'Desired room temperature', type: 'number' },
  { key: 'supplyTemperature', label: 'Supply temperature', type: 'number' },
  { key: 'heatOutput', label: 'Heat output', type: 'number' },
  { key: 'qNet', label: 'Q-net', type: 'number' },
  { key: 'envelopeLoss', label: 'Envelope loss', type: 'number' },
  { key: 'ventilationLoss', label: 'Ventilation loss', type: 'number' },
  { key: 'totalHeatLoss', label: 'Total heat loss', type: 'number' },
  { key: 'airChangeRate', label: 'Air change rate', type: 'number' },
  { key: 'currentCOP', label: 'Current COP', type: 'number' },
  { key: 'currentTemperature', label: 'Current room temperature', type: 'number' },
  { key: 'valvePosition', label: 'Valve position', type: 'number' },
  { key: 'thermostatLevel', label: 'Thermostat level', type: 'number' },
  { key: 'radiatorType', label: 'Radiator type', type: 'string' },
  { key: 'radiatorCount', label: 'Radiator count', type: 'number' },
  { key: 'systemPressure', label: 'System pressure', type: 'number' },
  { key: 'uValue', label: 'U-value', type: 'number' },
  { key: 'totalArea', label: 'Total area', type: 'number' },
  { key: 'roofArea', label: 'Roof area', type: 'number' },
  { key: 'wallArea', label: 'Wall area', type: 'number' },
  { key: 'windowArea', label: 'Window area', type: 'number' },
  { key: 'selectedCity', label: 'Selected city', type: 'string' },
]);

const CONFIGURATION_ITEMS = TRAINING_PROFILE_CONFIGURATION_GROUPS.flatMap((group) => group.items);
const CONFIGURATION_ITEM_MAP = new Map(CONFIGURATION_ITEMS.map((item) => [item.id, item]));
const CONDITION_FIELD_SET = new Set(CONDITION_FIELD_DEFINITIONS.map((field) => field.key));
const NUMERIC_CONDITION_FIELDS = new Set(CONDITION_FIELD_DEFINITIONS.filter((field) => field.type === 'number').map((field) => field.key));
const BOOLEAN_CONDITION_FIELDS = new Set(CONDITION_FIELD_DEFINITIONS.filter((field) => field.type === 'boolean').map((field) => field.key));
const CONDITION_OPERATOR_SET = new Set(CONDITION_OPERATOR_DEFINITIONS.map((operator) => operator.key));
const SPOTLIGHT_POSITION_SET = new Set(SPOTLIGHT_POSITION_DEFINITIONS.map((position) => position.key));
const KNOWN_ICON_KEYS = new Set(AVAILABLE_QUEST_ICONS.map((icon) => icon.key));
const ICON_LABEL_MAP = new Map(AVAILABLE_QUEST_ICONS.map((icon) => [icon.key, icon.label]));

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const hasAnyOwnKey = (source, keys) => keys.some((key) => Object.prototype.hasOwnProperty.call(source || {}, key));

const getFirstDefined = (source, keys) => {
  if (!isPlainObject(source)) {
    return undefined;
  }

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key) && source[key] !== undefined) {
      return source[key];
    }
  }

  return undefined;
};

const normalizeOptionalString = (value) => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const normalizeOptionalInteger = (value) => {
  if (typeof value === 'number' && Number.isInteger(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isInteger(parsed) ? parsed : undefined;
  }

  return undefined;
};

const normalizeOptionalIntegerArray = (values, legacyValue) => {
  const normalizedValues = (Array.isArray(values) ? values : [])
    .map((value) => normalizeOptionalInteger(value))
    .filter((value) => value !== undefined);

  if (normalizedValues.length === 0) {
    const legacyIndex = normalizeOptionalInteger(legacyValue);
    if (legacyIndex !== undefined) {
      normalizedValues.push(legacyIndex);
    }
  }

  return [...new Set(normalizedValues)].sort((left, right) => left - right);
};

const stripHtml = (value) => String(value || '')
  .replace(/<br\s*\/?>/gi, ' ')
  .replace(/<[^>]+>/g, ' ')
  .replace(/&nbsp;/gi, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const mapNullableString = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const getDefaultEditorValue = (item) => {
  if (item.editor?.defaultValue !== undefined) {
    return item.editor.defaultValue;
  }

  if (item.editor?.type === 'select') {
    return item.editor.options?.[0]?.value ?? null;
  }

  if (item.editor?.type === 'boolean') {
    return false;
  }

  return null;
};

const coerceConfigurationValue = (item, value) => {
  if (value === undefined || value === null || value === '') {
    return getDefaultEditorValue(item);
  }

  switch (item.editor?.type) {
    case 'number': {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : getDefaultEditorValue(item);
    }
    case 'boolean':
      return Boolean(value);
    case 'select':
      return String(value);
    default:
      return value;
  }
};

const validateConfigurationValue = (item, value, label) => {
  if (value === undefined || value === null) {
    return null;
  }

  switch (item.editor?.type) {
    case 'number':
      return Number.isFinite(Number(value)) ? null : `${item.title}: ${label} must be numeric.`;
    case 'boolean':
      return typeof value === 'boolean' ? null : `${item.title}: ${label} must be true or false.`;
    case 'select': {
      const valueAsString = String(value);
      const allowedValues = new Set((item.editor?.options || []).map((option) => String(option.value)));
      return allowedValues.has(valueAsString) ? null : `${item.title}: ${label} must use one of the configured option values.`;
    }
    default:
      return null;
  }
};

const serializeConfigurationDraft = (draft) => CONFIGURATION_ITEMS.reduce((nextDraft, item) => {
  const state = draft[item.id];
  nextDraft[item.id] = {
    visible: state?.visible !== false,
    editable: state?.editable !== false,
    defaultEnabled: Boolean(state?.defaultEnabled),
    defaultValue: state?.defaultValue,
    fixedEnabled: Boolean(state?.fixedEnabled),
    fixedValue: state?.fixedValue,
  };
  return nextDraft;
}, {});

const buildConfigurationCatalog = () => TRAINING_PROFILE_CONFIGURATION_GROUPS.map((group) => ({
  id: group.id,
  title: group.title,
  description: group.description,
  items: group.items.map((item) => ({
    id: item.id,
    title: item.title,
    description: item.description,
    controlKey: item.controlKey || null,
    controlAliases: item.controlAliases || [],
    valueKey: item.valueKey || null,
    valueAliases: item.valueAliases || [],
    supportsVisibility: Boolean(item.supportsVisibility),
    supportsEditability: Boolean(item.supportsEditability),
    supportsFixedValue: item.supportsFixedValue !== false,
    editor: item.editor || null,
  })),
}));

const TOP_LEVEL_JSON_PROPERTIES = Object.freeze([
  {
    name: 'Name',
    type: 'string',
    required: true,
    description: 'Training profile name shown to trainers and learners.',
  },
  {
    name: 'CountryId',
    type: 'integer | null',
    required: false,
    description: 'Country/language identifier. If omitted in the editor import flow, the current profile country is reused.',
  },
  {
    name: 'AdditionalTextRichContent',
    type: 'string | null',
    required: false,
    description: 'Profile-level rich HTML intro/description shown alongside the profile.',
  },
  {
    name: 'ConfigurationDraft',
    type: 'object keyed by configuration item id',
    required: false,
    description: 'Preferred authoring format for simulator UI visibility, editability, default values, and fixed values.',
  },
  {
    name: 'UiConfiguration',
    type: 'object | null',
    required: false,
    description: 'Raw advanced configuration payload. Supported, but ConfigurationDraft is safer and easier to author.',
  },
  {
    name: 'SimulationDefaults',
    type: 'object | null',
    required: false,
    description: 'Raw advanced default values payload. Usually derived automatically from ConfigurationDraft.',
  },
  {
    name: 'FixedValues',
    type: 'object | null',
    required: false,
    description: 'Raw advanced fixed-value payload. Usually derived automatically from ConfigurationDraft.',
  },
  {
    name: 'QuizAssignments',
    type: 'array of quiz assignment objects',
    required: false,
    description: 'Optional skill-quiz assignments. Omit to keep current assignments when importing into an existing profile.',
  },
  {
    name: 'TutorialQuests',
    type: 'array of tutorial quest objects',
    required: false,
    description: 'Quest definitions with nested steps, conditions, rich text, and spotlight metadata.',
  },
]);

const QUIZ_ASSIGNMENT_JSON_PROPERTIES = Object.freeze([
  {
    name: 'QuizCollectionId',
    type: 'integer',
    required: true,
    description: 'ID of an available quiz collection to assign to the training profile.',
  },
  {
    name: 'OrderIndex',
    type: 'integer',
    required: false,
    description: 'Display order of the assignment in the skill-test picker.',
  },
  {
    name: 'DisplayLabel',
    type: 'string | null',
    required: false,
    description: 'Optional learner-facing label shown instead of the quiz collection name.',
  },
]);

const QUEST_JSON_PROPERTIES = Object.freeze([
  {
    name: 'OrderIndex',
    type: 'integer',
    required: false,
    description: 'Quest ordering within the profile. If omitted, import order is used.',
  },
  {
    name: 'Title',
    type: 'string',
    required: true,
    description: 'Quest title shown on the quest button and in the quest modal header.',
  },
  {
    name: 'Category',
    type: 'string | null',
    required: false,
    description: 'Optional short category such as Commissioning, Optimization, or Diagnostics.',
  },
  {
    name: 'Difficulty',
    type: 'string | null',
    required: false,
    description: 'Optional difficulty badge text such as Beginner or Advanced.',
  },
  {
    name: 'EstimatedTime',
    type: 'string | null',
    required: false,
    description: 'Optional estimated duration label, for example 10 minutes.',
  },
  {
    name: 'IconKey',
    type: 'string | null',
    required: false,
    description: 'Quest icon key chosen from KnownQuestIcons.',
  },
  {
    name: 'IsChallenge',
    type: 'boolean',
    required: false,
    description: 'Marks the quest as a challenge/capstone quest in the modal and button styling.',
  },
  {
    name: 'LearningObjectivesRichContent',
    type: 'string | null',
    required: false,
    description: 'Rich HTML shown in the Learning objectives section of the quest modal.',
  },
  {
    name: 'ScenarioRichContent',
    type: 'string | null',
    required: false,
    description: 'Rich HTML shown in the Scenario section of the quest modal.',
  },
  {
    name: 'RealWorldConnectionRichContent',
    type: 'string | null',
    required: false,
    description: 'Trainer-facing or learner-facing real-world context preserved in profile data for future use and authoring clarity.',
  },
  {
    name: 'CompletionDescriptionRichContent',
    type: 'string | null',
    required: false,
    description: 'Rich HTML that explains what completion means for the quest.',
  },
  {
    name: 'KnowledgeSynthesisRichContent',
    type: 'string | null',
    required: false,
    description: 'Rich HTML shown after completion in the Knowledge synthesis section.',
  },
  {
    name: 'PracticalTipRichContent',
    type: 'string | null',
    required: false,
    description: 'Optional practical trainer tip stored with the quest authoring data.',
  },
  {
    name: 'ConditionAst',
    type: 'object | null',
    required: false,
    description: 'Quest-completion condition tree evaluated against live simulation state.',
  },
  {
    name: 'Steps',
    type: 'array of step objects',
    required: true,
    description: 'Ordered learner steps shown inside the collapsible Steps section and spotlight journey.',
  },
]);

const STEP_JSON_PROPERTIES = Object.freeze([
  {
    name: 'OrderIndex',
    type: 'integer',
    required: false,
    description: 'Step ordering within the quest. If omitted, import order is used.',
  },
  {
    name: 'StepKey',
    type: 'string | null',
    required: false,
    description: 'Stable authoring key such as startup-1 or 3a.',
  },
  {
    name: 'TaskTitle',
    type: 'string',
    required: true,
    description: 'Short step title shown in the collapse header and spotlight header.',
  },
  {
    name: 'InstructionRichContent',
    type: 'string | null',
    required: false,
    description: 'Primary learner instruction shown inside the step body and available to the spotlight journey.',
  },
  {
    name: 'DetailedGuidanceRichContent',
    type: 'string | null',
    required: false,
    description: 'Additional explanation, coaching, or reasoning support for the learner.',
  },
  {
    name: 'SuccessIndicatorRichContent',
    type: 'string | null',
    required: false,
    description: 'Signals what the learner should observe when the step is done correctly.',
  },
  {
    name: 'JustInTimeRichContent',
    type: 'string | null',
    required: false,
    description: 'Optional on-demand help content shown in a side panel beside the quest modal when the learner clicks the help icon for the step.',
  },
  {
    name: 'AnswerChecks',
    type: 'array of answer-check objects',
    required: false,
    description: 'Ordered learner answer checks. Each item is either an expected-value pair, a yes/no question, or a multiple-choice question with one or more correct choices.',
  },
  {
    name: 'LockNextStepsUntilCorrect',
    type: 'boolean',
    required: false,
    description: 'When true, later steps stay locked until the answer is correct or attempts are exhausted.',
  },
  {
    name: 'SpotlightTarget',
    type: 'object | null',
    required: false,
    description: 'Optional spotlight metadata used by the guided journey overlay.',
  },
]);

const ANSWER_CHECK_JSON_PROPERTIES = Object.freeze([
  {
    name: 'OrderIndex',
    type: 'integer',
    required: false,
    description: 'Order of this check within the step. If omitted, import order is used.',
  },
  {
    name: 'Name',
    type: 'string | null',
    required: false,
    description: 'Label for an expected-value check. Use together with Value only.',
  },
  {
    name: 'Value',
    type: 'string | null',
    required: false,
    description: 'Expected learner answer for an expected-value check. This must be a JSON string, not a number. Example: "35", not 35.',
  },
  {
    name: 'Question',
    type: 'string | null',
    required: false,
    description: 'Prompt text for a yes/no question. Use together with Correct only.',
  },
  {
    name: 'Correct',
    type: 'boolean | null',
    required: false,
    description: 'Correct answer for a yes/no question.',
  },
  {
    name: 'JustInTimeRichContent',
    type: 'string | null',
    required: false,
    description: 'Optional on-demand help content shown beside the quest modal when the learner clicks the answer-check help icon.',
  },
  {
    name: 'Options',
    type: 'array of strings | null',
    required: false,
    description: 'Choices for a multiple-choice question. Use together with Question and CorrectOptionIndexes.',
  },
  {
    name: 'CorrectOptionIndexes',
    type: 'array of integers | null',
    required: false,
    description: 'Zero-based indexes of the correct choices in Options. Provide at least one index.',
  },
  {
    name: 'CorrectOptionIndex',
    type: 'integer | null',
    required: false,
    description: 'Legacy single correct choice index in Options. Prefer CorrectOptionIndexes.',
  },
]);

const SPOTLIGHT_TARGET_JSON_PROPERTIES = Object.freeze([
  {
    name: 'selector',
    type: 'string',
    required: true,
    description: 'Stable simulator selector taken from AvailableSpotlightSelectors.',
  },
  {
    name: 'position',
    type: 'string',
    required: true,
    description: 'Tooltip placement relative to the highlighted element. Use top, bottom, left, or right.',
  },
  {
    name: 'displayName',
    type: 'string | null',
    required: false,
    description: 'Optional friendly name for the highlighted element.',
  },
  {
    name: 'fallbackRect',
    type: 'object | null',
    required: false,
    description: 'Optional fallback rectangle with x, y, width, and height when the selector cannot be found.',
  },
  {
    name: 'fallback',
    type: 'object | null',
    required: false,
    description: 'Legacy fallback rectangle alias still accepted by the parser.',
  },
]);

const CONDITION_AST_JSON_PROPERTIES = Object.freeze([
  {
    name: 'combinator',
    type: 'string',
    required: true,
    description: 'Logical combinator for child rules. Use and or or.',
  },
  {
    name: 'rules',
    type: 'array',
    required: true,
    description: 'Array of child condition nodes or leaf rules.',
  },
  {
    name: 'label',
    type: 'string | null',
    required: false,
    description: 'Optional human-readable explanation for a leaf rule.',
  },
]);

const CONDITION_RULE_JSON_PROPERTIES = Object.freeze([
  {
    name: 'field',
    type: 'string',
    required: true,
    description: 'Simulation metric field name from ConditionAst.fields.',
  },
  {
    name: 'operator',
    type: 'string',
    required: true,
    description: 'Comparison operator from ConditionAst.operators.',
  },
  {
    name: 'value',
    type: 'number | string | boolean | array | null',
    required: false,
    description: 'Operator-dependent comparison value. between expects an array. in expects an array. Numeric fields usually use numbers.',
  },
  {
    name: 'compareField',
    type: 'string | null',
    required: false,
    description: 'Used by field-multiplier operators to compare against another simulation field.',
  },
  {
    name: 'multiplier',
    type: 'number | null',
    required: false,
    description: 'Numeric multiplier used by field-multiplier operators.',
  },
  {
    name: 'tolerance',
    type: 'number | null',
    required: false,
    description: 'Tolerance used by approximately comparisons.',
  },
  {
    name: 'label',
    type: 'string | null',
    required: false,
    description: 'Optional readable explanation of the rule.',
  },
]);

const formatEditorDescription = (editor) => {
  if (!editor) {
    return 'visibility-only item without a value editor';
  }

  if (editor.type === 'number') {
    return `number input (min=${editor.min}, max=${editor.max}, step=${editor.step}, unit=${editor.unit || 'none'}, default=${editor.defaultValue})`;
  }

  if (editor.type === 'select') {
    const optionPreview = (editor.options || []).slice(0, 6).map((option) => option.value).join(', ');
    const suffix = (editor.options || []).length > 6 ? ', ...' : '';
    return `select input (options=${optionPreview}${suffix}, default=${editor.defaultValue})`;
  }

  if (editor.type === 'boolean') {
    return `boolean toggle (default=${editor.defaultValue === undefined ? false : editor.defaultValue})`;
  }

  return `${editor.type || 'custom'} editor`;
};

const buildConfigurationCatalogText = () => buildConfigurationCatalog().flatMap((group) => ([
  `${group.title}: ${group.description}`,
  ...group.items.map((item) => {
    const parts = [
      `${item.id}: ${item.title}`,
      item.description,
      `controlKey=${item.controlKey || 'none'}`,
      item.controlAliases.length > 0 ? `controlAliases=${item.controlAliases.join(', ')}` : null,
      `valueKey=${item.valueKey || 'none'}`,
      item.valueAliases.length > 0 ? `valueAliases=${item.valueAliases.join(', ')}` : null,
      `supportsVisibility=${item.supportsVisibility}`,
      `supportsEditability=${item.supportsEditability}`,
      `supportsFixedValue=${item.supportsFixedValue}`,
      `editor=${formatEditorDescription(item.editor)}`,
    ].filter(Boolean);

    return `- ${parts.join('. ')}.`;
  }),
]));

const buildPropertyGuideText = (sectionTitle, properties) => ([
  `${sectionTitle}:`,
  ...properties.map((property) => `- ${property.name}: ${property.type}. ${property.description}${property.required ? ' Required when used.' : ''}`),
]);

const buildSimulationToolOverview = () => ([
  'The heat pump simulation shows an external unit, a room unit, and a control panel for outdoor climate, building envelope, radiator selection, room targets, and system settings.',
  'Learners can start and pause the simulation, change outdoor conditions, inspect COP and temperature readings, and work through guided tutorial quests.',
  'The simulation exposes metrics such as outdoorTemperature, selectedRoomType, desiredTemperature, supplyTemperature, heatOutput, qNet, envelopeLoss, ventilationLoss, totalHeatLoss, airChangeRate, currentCOP, currentTemperature, valvePosition, thermostatLevel, radiatorType, radiatorCount, systemPressure, uValue, totalArea, roofArea, wallArea, windowArea, and selectedCity.',
  'Toolbar tools include the run button, radiator-rating overlay, heat-flux overlay, learning resources, and header visibility toggle.',
  'Supporting controls include city selection, map-based climate selection, building-era shortcuts, detailed envelope parameters, radiator type and size selection, room type presets, supply temperature control, system pressure, and weather-compensation helpers.',
]);

const buildSimulationUiComponentReference = () => ([
  {
    name: 'Thermostat and valve area',
    selector: "[data-testid='room-valve-thermostat-area']",
    role: 'Represents room-side heat delivery behaviour. It helps learners connect thermostat intent and valve opening with comfort, flow restriction, and how quickly the radiator can respond.',
    authoringUse: 'Use this when the step is about how the room responds to heat delivery or when learners should inspect the room-side control relationship before changing other settings.',
    avoid: 'Do not frame it as the only control that matters. It is one part of a larger system that also depends on outdoor conditions, supply temperature, emitter choice, and building losses.',
    suggestedDisplayNames: ['Thermostat and valve area', 'Room-side heat control', 'Thermostat and valve'],
  },
  {
    name: 'Preset valve',
    selector: "[data-testid='valve-preset']",
    role: 'The preset valve influences hydraulic restriction and flow balance through the emitter path. It is useful for explaining why heat delivery can change even when temperature settings stay similar.',
    authoringUse: 'Use this when the learner must adjust or compare valve presets, or when a quest explains balancing and why flow affects return temperature, heat output, or COP indirectly.',
    avoid: 'Do not overstate it as a magic efficiency control. It changes flow conditions and should usually be discussed together with radiator sizing, supply temperature, and room demand.',
    suggestedDisplayNames: ['Preset valve', 'Valve preset', 'Flow preset'],
  },
  {
    name: 'Radiator controls',
    selector: "[data-testid='radiator-controls']",
    role: 'The radiator section lets learners choose emitter type and size, then observe how emitter capacity changes heat output and the supply-temperature requirement.',
    authoringUse: 'Use this for emitter sizing, radiator-type comparison, under-sizing or over-sizing scenarios, and tradeoff discussions between radiator capacity and operating temperatures.',
    avoid: 'Do not reduce it to a cosmetic hardware choice. It meaningfully affects achievable heat output, but it still works within the limits of the rest of the system.',
    suggestedDisplayNames: ['Radiator controls', 'Radiator section', 'Emitter selection'],
  },
  {
    name: 'COP display',
    selector: "[data-testid='cop-display']",
    role: 'The COP indicator summarizes operating efficiency at the current state. It is an outcome signal that helps learners connect efficiency to temperature lift, emitter choices, and system demand.',
    authoringUse: 'Use this when the step asks the learner to interpret efficiency changes after adjusting supply temperature, climate inputs, or emitters.',
    avoid: 'Do not treat COP as an isolated score or the only success criterion. Good quests usually tie it back to comfort, heat output, and building demand.',
    suggestedDisplayNames: ['COP display', 'Efficiency indicator', 'COP indicator'],
  },
  {
    name: 'Supply temperature control',
    selector: "[data-testid='supply-temp-control']",
    role: 'This control sets the heating-water supply temperature and is central to the tradeoff between emitter output and efficiency.',
    authoringUse: 'Use this when learners must raise or lower delivery temperature, compare heat output, or reason about why COP changes as temperature lift changes.',
    avoid: 'Do not present it as a free improvement lever. Lower supply temperature can improve COP, but only if emitters and the building load still allow the room to stay comfortable.',
    suggestedDisplayNames: ['Supply temperature', 'Supply temperature control', 'Heating-water temperature'],
  },
  {
    name: 'Outdoor temperature input',
    selector: "[data-testid='outdoor-temperature-input']",
    role: 'Outdoor climate drives heat demand and strongly influences operating conditions. It is a core scenario-setting input rather than a learner answer field.',
    authoringUse: 'Use this to establish scenario conditions such as cold-weather startup, mild-weather optimisation, or climate comparison tasks.',
    avoid: 'Do not convert this into an AnswerChecks expected value when the learner is supposed to set or observe it in the simulator itself.',
    suggestedDisplayNames: ['Outdoor temperature', 'Outdoor temperature input', 'Climate setting'],
  },
]);

const buildQuestModalFeatureReference = () => ([
  'The quest modal shows quest metadata, an always-available Start Journey button, and a completion banner when the quest is completed.',
  'Quest sections can include Learning objectives, Scenario, Steps, Completion conditions, and Knowledge synthesis content.',
  'Steps render as collapsible panels with a short task title plus rich HTML instruction, detailed guidance, and success indicator content.',
  'A step can optionally include ordered AnswerChecks using expected-value pairs, yes/no questions, or multiple-choice questions. Expected-value checks are validated as text answers.',
  'If LockNextStepsUntilCorrect is true, later steps remain locked until the learner answers correctly or the attempt limit is reached.',
  'SpotlightTarget lets the Start Journey mode highlight simulator UI elements with a guided overlay card.',
  'Quest completion conditions are driven by ConditionAst and are evaluated against live simulation metrics. Completed quests can be reset and run again.',
  'In learner flows outside profile mode, completed quests can expose a follow-up quiz action. In training-profile mode, assigned skill quizzes are managed separately through quiz assignments.',
]);

const buildSpotlightAuthoringGuidance = () => ({
  rules: [
    'If a step includes SpotlightTarget, also write TaskTitle, InstructionRichContent, DetailedGuidanceRichContent, and SuccessIndicatorRichContent that clearly reference the highlighted UI element.',
    'Use DisplayName as a short UI label, usually 2 to 5 words. It should identify the highlighted control, not restate the whole learning objective.',
    'Keep spotlight-related instructional text action-oriented: tell the learner what to inspect, change, or compare on the highlighted control and why it matters in the system.',
    'When a selector highlights a broader area such as the radiator section or thermostat area, the step text should name the specific sub-control or relationship the learner should focus on.',
  ],
  examples: [
    {
      selector: "[data-testid='outdoor-temperature-input']",
      displayName: 'Outdoor temperature',
      taskTitle: 'Set the cold-weather starting point',
      instructionRichContent: '<p>Use the outdoor temperature control to set the scenario to <strong>-7 C</strong> so the system starts under winter demand.</p>',
      detailedGuidanceRichContent: '<p>This control defines the climate boundary condition for the whole exercise. A colder outside temperature increases heat demand and changes the efficiency challenge the learner must manage.</p>',
      successIndicatorRichContent: '<p>The outdoor temperature is set to the requested winter condition and the learner is ready to continue with the next system decision.</p>',
    },
    {
      selector: "[data-testid='valve-preset']",
      displayName: 'Preset valve',
      taskTitle: 'Inspect the room-side flow restriction',
      instructionRichContent: '<p>Focus on the preset valve and compare how changing the preset affects the room-side heat delivery path.</p>',
      detailedGuidanceRichContent: '<p>The valve preset is not the whole story, but it changes hydraulic resistance and helps explain why heat output and return temperature can shift even before other settings change.</p>',
      successIndicatorRichContent: '<p>The learner can point to the preset valve and explain its role in the room-side flow path.</p>',
    },
    {
      selector: "[data-testid='cop-display']",
      displayName: 'COP display',
      taskTitle: 'Read the efficiency response',
      instructionRichContent: '<p>Check the COP display after your setting changes and note whether efficiency improved or fell.</p>',
      detailedGuidanceRichContent: '<p>The COP display is an outcome indicator. Use it together with heat output, comfort, and the selected temperatures to judge whether the new operating point is actually better.</p>',
      successIndicatorRichContent: '<p>The learner has interpreted the COP reading in context instead of treating it as a standalone score.</p>',
    },
  ],
});

const buildAnswerCheckDecisionGuide = () => ({
  useAnswerChecksFor: [
    'Learner-entered text answers inside the step panel, such as a typed calculation result, a short justification, a yes/no prompt, or a multiple-choice reflection.',
    'Expected-value answers only when the learner is answering through the quest UI itself rather than by changing the simulator controls.',
  ],
  useConditionAstFor: [
    'Live simulator-state validation such as outdoor temperature equals -7, supply temperature reaches a threshold, selected city is correct, qNet is non-negative, COP improves, valve position is in range, or the simulation is running.',
    'Checks that depend on values shown by the simulator or values the learner changes directly in simulator controls.',
  ],
  useConfigurationDraftFor: [
    'Initial scenario setup or locked profile behaviour, such as starting outdoor temperature, selected city, room type, radiator defaults, or fixed supply temperature.',
  ],
  antiPatterns: [
    'Do not create an AnswerChecks expected-value item just to ask for a value the learner must set in the simulator control itself.',
    'Do not use AnswerChecks to verify state that the backend ConditionAst can evaluate directly from simulation metrics.',
    'Do not ask the learner to type back a visible control value such as outdoor temperature, COP, or selected city unless the pedagogical goal is explicitly a text-entry exercise and the value is not already being validated elsewhere.',
  ],
  examples: [
    {
      bad: 'AnswerChecks asks for outdoor temperature = "-7" after the learner already sets that in the outdoor temperature input.',
      good: 'Set the starting climate through ConfigurationDraft or step instructions, then use ConditionAst with field outdoorTemperature and operator equals to confirm the simulator reached -7.',
    },
    {
      bad: 'AnswerChecks asks the learner to type the current COP value after reading the COP display.',
      good: 'Use the spotlight and rich text to direct attention to the COP display, and use ConditionAst only if the quest should require COP to pass a threshold.',
    },
  ],
});

const buildAuthoringIdeas = () => ([
  'Write quests as scenario-based labs: startup checks, climate selection, radiator sizing, supply-temperature optimization, ventilation diagnosis, and learner reflection.',
  'Use challenge quests for capstones where the learner must justify a setting choice and enter a calculated answer as text.',
  'Use LearningObjectivesRichContent for what the learner should master, ScenarioRichContent for the field situation, CompletionDescriptionRichContent for the success target, and KnowledgeSynthesisRichContent for the takeaway after completion.',
  'Use SpotlightTarget on steps that should direct the learner to a specific UI element, and use ConditionAst on quests that should complete only when measurable simulator conditions are satisfied.',
]);

const buildTemplateConfigurationDraft = (profile) => {
  const templateDraft = serializeConfigurationDraft(createTrainingProfileConfigurationDraft({
    uiConfiguration: profile?.uiConfiguration,
    simulationDefaults: profile?.simulationDefaults,
    fixedValues: profile?.fixedValues,
  }));

  if (templateDraft.outdoorTemperature) {
    templateDraft.outdoorTemperature = {
      ...templateDraft.outdoorTemperature,
      visible: true,
      editable: true,
      defaultEnabled: true,
      defaultValue: -7,
      fixedEnabled: false,
      fixedValue: null,
    };
  }

  if (templateDraft.selectedCity) {
    templateDraft.selectedCity = {
      ...templateDraft.selectedCity,
      visible: true,
      editable: true,
      defaultEnabled: true,
      defaultValue: 'aachen',
      fixedEnabled: false,
      fixedValue: null,
    };
  }

  if (templateDraft.desiredTemperature) {
    templateDraft.desiredTemperature = {
      ...templateDraft.desiredTemperature,
      visible: true,
      editable: true,
      defaultEnabled: true,
      defaultValue: 20,
      fixedEnabled: false,
      fixedValue: null,
    };
  }

  if (templateDraft.selectedRoomType) {
    templateDraft.selectedRoomType = {
      ...templateDraft.selectedRoomType,
      visible: true,
      editable: true,
      defaultEnabled: true,
      defaultValue: 'living',
      fixedEnabled: false,
      fixedValue: null,
    };
  }

  if (templateDraft.currentTemperature) {
    templateDraft.currentTemperature = {
      ...templateDraft.currentTemperature,
      defaultEnabled: true,
      defaultValue: 18,
    };
  }

  if (templateDraft.supplyTemperature) {
    templateDraft.supplyTemperature = {
      ...templateDraft.supplyTemperature,
      visible: true,
      editable: true,
      defaultEnabled: true,
      defaultValue: 38,
      fixedEnabled: false,
      fixedValue: null,
    };
  }

  if (templateDraft.systemPressure) {
    templateDraft.systemPressure = {
      ...templateDraft.systemPressure,
      visible: true,
      editable: true,
      defaultEnabled: true,
      defaultValue: 0.2,
      fixedEnabled: false,
      fixedValue: null,
    };
  }

  if (templateDraft.radiatorType) {
    templateDraft.radiatorType = {
      ...templateDraft.radiatorType,
      visible: true,
      editable: true,
      defaultEnabled: true,
      defaultValue: '22',
      fixedEnabled: false,
      fixedValue: null,
    };
  }

  if (templateDraft.radiatorSize) {
    templateDraft.radiatorSize = {
      ...templateDraft.radiatorSize,
      visible: true,
      editable: true,
      defaultEnabled: true,
      defaultValue: 60,
      fixedEnabled: false,
      fixedValue: null,
    };
  }

  return templateDraft;
};

const normalizeStep = (step) => ({
  orderIndex: normalizeOptionalInteger(getFirstDefined(step, ['OrderIndex', 'orderIndex'])),
  stepKey: normalizeOptionalString(getFirstDefined(step, ['StepKey', 'stepKey'])),
  taskTitle: normalizeOptionalString(getFirstDefined(step, ['TaskTitle', 'taskTitle'])),
  instructionRichContent: getFirstDefined(step, ['InstructionRichContent', 'instructionRichContent']),
  detailedGuidanceRichContent: getFirstDefined(step, ['DetailedGuidanceRichContent', 'detailedGuidanceRichContent']),
  successIndicatorRichContent: getFirstDefined(step, ['SuccessIndicatorRichContent', 'successIndicatorRichContent']),
  justInTimeRichContent: getFirstDefined(step, ['JustInTimeRichContent', 'justInTimeRichContent']),
  answerChecks: (() => {
    const rawAnswerChecks = getFirstDefined(step, ['AnswerChecks', 'answerChecks']);
    if (Array.isArray(rawAnswerChecks)) {
      return rawAnswerChecks.map((check, index) => {
        const correctOptionIndexes = normalizeOptionalIntegerArray(
          getFirstDefined(check, ['CorrectOptionIndexes', 'correctOptionIndexes']),
          getFirstDefined(check, ['CorrectOptionIndex', 'correctOptionIndex'])
        );

        return {
          orderIndex: normalizeOptionalInteger(getFirstDefined(check, ['OrderIndex', 'orderIndex'])) ?? index,
          name: normalizeOptionalString(getFirstDefined(check, ['Name', 'name'])),
          value: normalizeOptionalString(getFirstDefined(check, ['Value', 'value'])),
          question: normalizeOptionalString(getFirstDefined(check, ['Question', 'question'])),
          correct: typeof getFirstDefined(check, ['Correct', 'correct']) === 'boolean'
            ? getFirstDefined(check, ['Correct', 'correct'])
            : null,
          justInTimeRichContent: getFirstDefined(check, ['JustInTimeRichContent', 'justInTimeRichContent']),
          options: Array.isArray(getFirstDefined(check, ['Options', 'options']))
            ? getFirstDefined(check, ['Options', 'options'])
              .map((option) => normalizeOptionalString(option))
              .filter(Boolean)
            : [],
          correctOptionIndexes,
          correctOptionIndex: correctOptionIndexes.length === 1 ? correctOptionIndexes[0] : null,
        };
      });
    }

    const legacyExpectedValueName = normalizeOptionalString(getFirstDefined(step, ['ExpectedValueName', 'expectedValueName']));
    const legacyExpectedValue = normalizeOptionalString(getFirstDefined(step, ['ExpectedValue', 'expectedValue']));
    return legacyExpectedValueName || legacyExpectedValue
      ? [{ orderIndex: 0, name: legacyExpectedValueName, value: legacyExpectedValue, question: null, correct: null }]
      : [];
  })(),
  lockNextStepsUntilCorrect: Boolean(getFirstDefined(step, ['LockNextStepsUntilCorrect', 'lockNextStepsUntilCorrect'])),
  spotlightTarget: getFirstDefined(step, ['SpotlightTarget', 'spotlightTarget']),
});

const normalizeQuest = (quest) => {
  const rawSteps = getFirstDefined(quest, ['Steps', 'steps']);

  return {
    orderIndex: normalizeOptionalInteger(getFirstDefined(quest, ['OrderIndex', 'orderIndex'])),
    title: normalizeOptionalString(getFirstDefined(quest, ['Title', 'title'])),
    category: normalizeOptionalString(getFirstDefined(quest, ['Category', 'category'])),
    difficulty: normalizeOptionalString(getFirstDefined(quest, ['Difficulty', 'difficulty'])),
    estimatedTime: normalizeOptionalString(getFirstDefined(quest, ['EstimatedTime', 'estimatedTime'])),
    iconKey: normalizeOptionalString(getFirstDefined(quest, ['IconKey', 'iconKey'])),
    isChallenge: Boolean(getFirstDefined(quest, ['IsChallenge', 'isChallenge'])),
    learningObjectivesRichContent: getFirstDefined(quest, ['LearningObjectivesRichContent', 'learningObjectivesRichContent']),
    scenarioRichContent: getFirstDefined(quest, ['ScenarioRichContent', 'scenarioRichContent']),
    realWorldConnectionRichContent: getFirstDefined(quest, ['RealWorldConnectionRichContent', 'realWorldConnectionRichContent']),
    completionDescriptionRichContent: getFirstDefined(quest, ['CompletionDescriptionRichContent', 'completionDescriptionRichContent']),
    knowledgeSynthesisRichContent: getFirstDefined(quest, ['KnowledgeSynthesisRichContent', 'knowledgeSynthesisRichContent']),
    practicalTipRichContent: getFirstDefined(quest, ['PracticalTipRichContent', 'practicalTipRichContent']),
    conditionAst: getFirstDefined(quest, ['ConditionAst', 'conditionAst']),
    steps: Array.isArray(rawSteps) ? rawSteps.map(normalizeStep) : [],
  };
};

const mapStepForRequest = (step, index) => ({
  orderIndex: Number.isInteger(step.orderIndex) ? step.orderIndex : index,
  stepKey: mapNullableString(step.stepKey),
  taskTitle: step.taskTitle || '',
  instructionRichContent: mapNullableString(step.instructionRichContent),
  detailedGuidanceRichContent: mapNullableString(step.detailedGuidanceRichContent),
  successIndicatorRichContent: mapNullableString(step.successIndicatorRichContent),
  justInTimeRichContent: mapNullableString(step.justInTimeRichContent),
  answerChecks: (Array.isArray(step.answerChecks) ? step.answerChecks : []).map((check, checkIndex) => {
    const correctOptionIndexes = normalizeOptionalIntegerArray(check.correctOptionIndexes, check.correctOptionIndex);
    return {
      orderIndex: Number.isInteger(check.orderIndex) ? check.orderIndex : checkIndex,
      name: mapNullableString(check.name),
      value: mapNullableString(check.value),
      question: mapNullableString(check.question),
      correct: typeof check.correct === 'boolean' ? check.correct : null,
      justInTimeRichContent: mapNullableString(check.justInTimeRichContent),
      options: Array.isArray(check.options)
        ? check.options.map((option) => mapNullableString(option)).filter(Boolean)
        : [],
      correctOptionIndexes,
      correctOptionIndex: correctOptionIndexes.length === 1 ? correctOptionIndexes[0] : null,
    };
  }),
  lockNextStepsUntilCorrect: Boolean(step.lockNextStepsUntilCorrect),
  spotlightTarget: step.spotlightTarget ?? null,
});

const mapQuestForRequest = (quest, index) => ({
  orderIndex: Number.isInteger(quest.orderIndex) ? quest.orderIndex : index,
  title: quest.title || '',
  category: mapNullableString(quest.category),
  difficulty: mapNullableString(quest.difficulty),
  estimatedTime: mapNullableString(quest.estimatedTime),
  iconKey: mapNullableString(quest.iconKey),
  isChallenge: Boolean(quest.isChallenge),
  learningObjectivesRichContent: mapNullableString(quest.learningObjectivesRichContent),
  scenarioRichContent: mapNullableString(quest.scenarioRichContent),
  realWorldConnectionRichContent: mapNullableString(quest.realWorldConnectionRichContent),
  completionDescriptionRichContent: mapNullableString(quest.completionDescriptionRichContent),
  knowledgeSynthesisRichContent: mapNullableString(quest.knowledgeSynthesisRichContent),
  practicalTipRichContent: mapNullableString(quest.practicalTipRichContent),
  conditionAst: quest.conditionAst ?? null,
  steps: quest.steps.map((step, stepIndex) => mapStepForRequest(step, stepIndex)),
});

const mapStepForExport = (step, index) => ({
  OrderIndex: Number.isInteger(step?.orderIndex) ? step.orderIndex : index,
  StepKey: mapNullableString(step?.stepKey),
  TaskTitle: step?.taskTitle || '',
  InstructionRichContent: mapNullableString(step?.instructionRichContent),
  DetailedGuidanceRichContent: mapNullableString(step?.detailedGuidanceRichContent),
  SuccessIndicatorRichContent: mapNullableString(step?.successIndicatorRichContent),
  JustInTimeRichContent: mapNullableString(step?.justInTimeRichContent),
  AnswerChecks: Array.isArray(step?.answerChecks)
    ? step.answerChecks.map((check, checkIndex) => {
        const correctOptionIndexes = normalizeOptionalIntegerArray(check?.correctOptionIndexes, check?.correctOptionIndex);
        return {
          OrderIndex: Number.isInteger(check?.orderIndex) ? check.orderIndex : checkIndex,
          Name: mapNullableString(check?.name),
          Value: mapNullableString(check?.value),
          Question: mapNullableString(check?.question),
          Correct: typeof check?.correct === 'boolean' ? check.correct : null,
          JustInTimeRichContent: mapNullableString(check?.justInTimeRichContent),
          Options: Array.isArray(check?.options)
            ? check.options.map((option) => mapNullableString(option)).filter(Boolean)
            : [],
          CorrectOptionIndexes: correctOptionIndexes,
          CorrectOptionIndex: correctOptionIndexes.length === 1 ? correctOptionIndexes[0] : null,
        };
      })
    : [],
  LockNextStepsUntilCorrect: Boolean(step?.lockNextStepsUntilCorrect),
  SpotlightTarget: step?.spotlightTarget ?? null,
});

const mapQuestForExport = (quest, index) => ({
  OrderIndex: Number.isInteger(quest?.orderIndex) ? quest.orderIndex : index,
  Title: quest?.title || '',
  Category: mapNullableString(quest?.category),
  Difficulty: mapNullableString(quest?.difficulty),
  EstimatedTime: mapNullableString(quest?.estimatedTime),
  IconKey: mapNullableString(quest?.iconKey),
  IsChallenge: Boolean(quest?.isChallenge),
  LearningObjectivesRichContent: mapNullableString(quest?.learningObjectivesRichContent),
  ScenarioRichContent: mapNullableString(quest?.scenarioRichContent),
  RealWorldConnectionRichContent: mapNullableString(quest?.realWorldConnectionRichContent),
  CompletionDescriptionRichContent: mapNullableString(quest?.completionDescriptionRichContent),
  KnowledgeSynthesisRichContent: mapNullableString(quest?.knowledgeSynthesisRichContent),
  PracticalTipRichContent: mapNullableString(quest?.practicalTipRichContent),
  ConditionAst: quest?.conditionAst ?? null,
  Steps: Array.isArray(quest?.steps)
    ? quest.steps.map((step, stepIndex) => mapStepForExport(step, stepIndex))
    : [],
});

const validateJsonTree = (value, label, maxDepth, maxCount) => {
  if (value === null || value === undefined) {
    return null;
  }

  let count = 0;

  const visit = (node, depth) => {
    if (depth > maxDepth) {
      return `${label} exceeds maximum nesting depth of ${maxDepth}.`;
    }

    if (Array.isArray(node)) {
      for (const entry of node) {
        count += 1;
        if (count > maxCount) {
          return `${label} exceeds maximum item count of ${maxCount}.`;
        }

        const error = visit(entry, depth + 1);
        if (error) {
          return error;
        }
      }

      return null;
    }

    if (isPlainObject(node)) {
      for (const entry of Object.values(node)) {
        count += 1;
        if (count > maxCount) {
          return `${label} exceeds maximum property count of ${maxCount}.`;
        }

        const error = visit(entry, depth + 1);
        if (error) {
          return error;
        }
      }
    }

    return null;
  };

  return visit(value, 0);
};

const validateConditionValue = (field, operator, value) => {
  if (operator === 'between') {
    if (!Array.isArray(value) || value.length !== 2) {
      return 'The between operator requires a two-item value array.';
    }

    if (NUMERIC_CONDITION_FIELDS.has(field) && value.some((entry) => typeof entry !== 'number')) {
      return `Condition field '${field}' requires numeric between values.`;
    }

    return null;
  }

  if (operator === 'in') {
    return Array.isArray(value) ? null : 'The in operator requires an array value.';
  }

  if (NUMERIC_CONDITION_FIELDS.has(field) && typeof value !== 'number') {
    return `Condition field '${field}' requires a numeric value.`;
  }

  if (BOOLEAN_CONDITION_FIELDS.has(field) && typeof value !== 'boolean') {
    return `Condition field '${field}' requires a boolean value.`;
  }

  if (!NUMERIC_CONDITION_FIELDS.has(field) && !BOOLEAN_CONDITION_FIELDS.has(field)
    && typeof value !== 'string' && !Array.isArray(value)) {
    return `Condition field '${field}' requires a string or array value.`;
  }

  return null;
};

const validateConditionNode = (node, depth, stats) => {
  if (depth > 8) {
    return 'Condition AST exceeds maximum nesting depth of 8.';
  }

  if (!isPlainObject(node)) {
    return 'Condition AST nodes must be objects.';
  }

  if (Array.isArray(node.rules)) {
    const combinator = normalizeOptionalString(node.combinator) || normalizeOptionalString(node.condition) || 'and';
    if (combinator !== 'and' && combinator !== 'or') {
      return "Condition groups must use combinator 'and' or 'or'.";
    }

    for (const child of node.rules) {
      const error = validateConditionNode(child, depth + 1, stats);
      if (error) {
        return error;
      }
    }

    return null;
  }

  stats.ruleCount += 1;
  if (stats.ruleCount > 100) {
    return 'Condition AST cannot contain more than 100 rules.';
  }

  const field = normalizeOptionalString(node.field);
  if (!field || !CONDITION_FIELD_SET.has(field)) {
    return `Condition field '${field || '<missing>'}' is not allowed.`;
  }

  const operator = normalizeOptionalString(node.operator);
  if (!operator || !CONDITION_OPERATOR_SET.has(operator)) {
    return `Condition operator '${operator || '<missing>'}' is not allowed.`;
  }

  if (operator.includes('FieldMultiplier')) {
    const compareField = normalizeOptionalString(node.compareField);
    if (!compareField || !CONDITION_FIELD_SET.has(compareField) || !NUMERIC_CONDITION_FIELDS.has(compareField)) {
      return 'Cross-field multiplier conditions require an allowed numeric compareField.';
    }

    if (typeof node.multiplier !== 'number') {
      return 'Cross-field multiplier conditions require a numeric multiplier.';
    }

    return null;
  }

  if (!Object.prototype.hasOwnProperty.call(node, 'value')) {
    return 'Condition rule requires a value.';
  }

  return validateConditionValue(field, operator, node.value);
};

const validateConditionAst = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const treeError = validateJsonTree(value, 'Condition AST', 8, 100);
  if (treeError) {
    return treeError;
  }

  const stats = { ruleCount: 0 };
  return validateConditionNode(value, 0, stats);
};

const validateSpotlightTarget = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const treeError = validateJsonTree(value, 'Spotlight target', 4, 25);
  if (treeError) {
    return treeError;
  }

  if (!isPlainObject(value)) {
    return 'Spotlight target must be an object.';
  }

  if (value.position !== undefined && !SPOTLIGHT_POSITION_SET.has(value.position)) {
    return 'Spotlight target position must be one of: top, bottom, left, right.';
  }

  if (value.selector !== undefined && typeof value.selector !== 'string') {
    return 'Spotlight target selector must be a string.';
  }

  if (value.displayName !== undefined && typeof value.displayName !== 'string') {
    return 'Spotlight target displayName must be a string.';
  }

  if (value.fallbackRect !== undefined && !isPlainObject(value.fallbackRect)) {
    return 'Spotlight target fallbackRect must be an object.';
  }

  if (value.fallback !== undefined && !isPlainObject(value.fallback)) {
    return 'Spotlight target fallback must be an object.';
  }

  return null;
};

const validateRichContentLength = (value, label) => {
  if (typeof value !== 'string') {
    return null;
  }

  return value.trim().length > 5000 ? `${label} content cannot exceed 5000 characters.` : null;
};

const validateStep = (step, stepIndex) => {
  const errors = [];

  if (!step.taskTitle) {
    errors.push('TaskTitle is required.');
  } else if (step.taskTitle.length > 255) {
    errors.push('TaskTitle cannot exceed 255 characters.');
  }

  if (step.stepKey && step.stepKey.length > 50) {
    errors.push('StepKey cannot exceed 50 characters.');
  }

  const answerChecks = Array.isArray(step.answerChecks) ? step.answerChecks : [];
  if (answerChecks.length > 20) {
    errors.push('AnswerChecks cannot contain more than 20 items.');
  }

  answerChecks.forEach((check, answerCheckIndex) => {
    const options = Array.isArray(check?.options)
      ? check.options.map((option) => (typeof option === 'string' ? option.trim() : '')).filter(Boolean)
      : [];
    const correctOptionIndexes = normalizeOptionalIntegerArray(check?.correctOptionIndexes, check?.correctOptionIndex);
    const hasExpectedValueShape = typeof check?.name === 'string' || typeof check?.value === 'string';
    const hasMultipleChoiceShape = options.length > 0 || correctOptionIndexes.length > 0;
    const hasYesNoShape = !hasMultipleChoiceShape && (typeof check?.question === 'string' || typeof check?.correct === 'boolean');

    if (!hasExpectedValueShape && !hasYesNoShape && !hasMultipleChoiceShape) {
      errors.push(`AnswerChecks[${answerCheckIndex}] must define either Name/Value, Question/Correct, or Question/Options/CorrectOptionIndexes.`);
      return;
    }

    if ((hasExpectedValueShape && hasYesNoShape)
      || (hasExpectedValueShape && hasMultipleChoiceShape)
      || (hasMultipleChoiceShape && typeof check?.correct === 'boolean')) {
      errors.push(`AnswerChecks[${answerCheckIndex}] cannot mix expected-value, yes/no-question, and multiple-choice fields.`);
      return;
    }

    if (hasExpectedValueShape) {
      if (!check.name || !check.value) {
        errors.push(`AnswerChecks[${answerCheckIndex}] expected-value checks require both Name and Value.`);
      }

      if (check.name && check.name.length > 255) {
        errors.push(`AnswerChecks[${answerCheckIndex}] Name cannot exceed 255 characters.`);
      }

      if (check.value && check.value.length > 255) {
        errors.push(`AnswerChecks[${answerCheckIndex}] Value cannot exceed 255 characters.`);
      }
    }

    if (hasMultipleChoiceShape) {
      if (!check.question || correctOptionIndexes.length < 1 || options.length < 2) {
        errors.push(`AnswerChecks[${answerCheckIndex}] multiple-choice questions require Question, at least two Options, and at least one correct choice.`);
      }

      if (check.question && check.question.length > 255) {
        errors.push(`AnswerChecks[${answerCheckIndex}] Question cannot exceed 255 characters.`);
      }

      if (options.length > 6) {
        errors.push(`AnswerChecks[${answerCheckIndex}] multiple-choice questions cannot exceed 6 options.`);
      }

      if (options.some((option) => option.length > 255)) {
        errors.push(`AnswerChecks[${answerCheckIndex}] Options cannot exceed 255 characters each.`);
      }

      if (correctOptionIndexes.some((correctOptionIndex) => correctOptionIndex < 0 || correctOptionIndex >= options.length)) {
        errors.push(`AnswerChecks[${answerCheckIndex}] CorrectOptionIndexes contains an out-of-range value.`);
      }
    }

    if (hasYesNoShape) {
      if (!check.question || typeof check.correct !== 'boolean') {
        errors.push(`AnswerChecks[${answerCheckIndex}] yes/no questions require both Question and Correct.`);
      }

      if (check.question && check.question.length > 255) {
        errors.push(`AnswerChecks[${answerCheckIndex}] Question cannot exceed 255 characters.`);
      }
    }

    const answerCheckContentError = validateRichContentLength(check.justInTimeRichContent, `AnswerChecks[${answerCheckIndex}].JustInTimeRichContent`);
    if (answerCheckContentError) {
      errors.push(answerCheckContentError);
    }
  });

  if (step.lockNextStepsUntilCorrect && answerChecks.length === 0) {
    errors.push('LockNextStepsUntilCorrect requires at least one AnswerChecks item.');
  }

  const contentError = validateRichContentLength(step.instructionRichContent, 'InstructionRichContent')
    || validateRichContentLength(step.detailedGuidanceRichContent, 'DetailedGuidanceRichContent')
    || validateRichContentLength(step.successIndicatorRichContent, 'SuccessIndicatorRichContent')
    || validateRichContentLength(step.justInTimeRichContent, 'JustInTimeRichContent');

  if (contentError) {
    errors.push(contentError);
  }

  const spotlightError = validateSpotlightTarget(step.spotlightTarget);
  if (spotlightError) {
    errors.push(spotlightError);
  }

  return errors.map((error) => `Step ${stepIndex + 1}: ${error}`);
};

const validateQuest = (quest) => {
  const errors = [];

  if (!quest.title) {
    errors.push('Title is required.');
  } else if (quest.title.length > 255) {
    errors.push('Title cannot exceed 255 characters.');
  }

  if (quest.category && quest.category.length > 100) {
    errors.push('Category cannot exceed 100 characters.');
  }

  if (quest.difficulty && quest.difficulty.length > 50) {
    errors.push('Difficulty cannot exceed 50 characters.');
  }

  if (quest.estimatedTime && quest.estimatedTime.length > 50) {
    errors.push('EstimatedTime cannot exceed 50 characters.');
  }

  if (quest.iconKey && quest.iconKey.length > 100) {
    errors.push('IconKey cannot exceed 100 characters.');
  }

  const contentError = validateRichContentLength(quest.learningObjectivesRichContent, 'LearningObjectivesRichContent')
    || validateRichContentLength(quest.scenarioRichContent, 'ScenarioRichContent')
    || validateRichContentLength(quest.realWorldConnectionRichContent, 'RealWorldConnectionRichContent')
    || validateRichContentLength(quest.completionDescriptionRichContent, 'CompletionDescriptionRichContent')
    || validateRichContentLength(quest.knowledgeSynthesisRichContent, 'KnowledgeSynthesisRichContent')
    || validateRichContentLength(quest.practicalTipRichContent, 'PracticalTipRichContent');

  if (contentError) {
    errors.push(contentError);
  }

  const conditionError = validateConditionAst(quest.conditionAst);
  if (conditionError) {
    errors.push(conditionError);
  }

  quest.steps.forEach((step, stepIndex) => {
    errors.push(...validateStep(step, stepIndex));
  });

  return errors;
};

const getConfigurationDraftSource = (rootPayload, profile) => {
  const rawDraft = getFirstDefined(rootPayload, ['ConfigurationDraft', 'configurationDraft']);
  if (rawDraft !== undefined) {
    return { kind: 'draft', value: rawDraft, provided: true };
  }

  const rawUiConfiguration = getFirstDefined(rootPayload, ['UiConfiguration', 'uiConfiguration']);
  const rawSimulationDefaults = getFirstDefined(rootPayload, ['SimulationDefaults', 'simulationDefaults']);
  const rawFixedValues = getFirstDefined(rootPayload, ['FixedValues', 'fixedValues']);

  if (rawUiConfiguration !== undefined || rawSimulationDefaults !== undefined || rawFixedValues !== undefined) {
    return {
      kind: 'raw',
      provided: true,
      value: {
        uiConfiguration: rawUiConfiguration,
        simulationDefaults: rawSimulationDefaults,
        fixedValues: rawFixedValues,
      },
    };
  }

  return {
    kind: 'current',
    provided: false,
    value: {
      uiConfiguration: profile?.uiConfiguration,
      simulationDefaults: profile?.simulationDefaults,
      fixedValues: profile?.fixedValues,
    },
  };
};

const normalizeConfigurationDraft = (rootPayload, profile) => {
  const errors = [];
  const source = getConfigurationDraftSource(rootPayload, profile);

  const fallbackDraft = createTrainingProfileConfigurationDraft({
    uiConfiguration: profile?.uiConfiguration,
    simulationDefaults: profile?.simulationDefaults,
    fixedValues: profile?.fixedValues,
  });

  let nextDraft = { ...fallbackDraft };

  if (source.kind === 'draft') {
    const draftEntries = Array.isArray(source.value)
      ? source.value.reduce((nextEntries, entry) => {
          if (!isPlainObject(entry)) {
            return nextEntries;
          }

          const itemId = normalizeOptionalString(getFirstDefined(entry, ['ItemId', 'itemId', 'Id', 'id', 'Key', 'key']));
          if (itemId) {
            nextEntries[itemId] = entry;
          }
          return nextEntries;
        }, {})
      : (isPlainObject(source.value) ? source.value : {});

    Object.keys(draftEntries).forEach((itemId) => {
      const item = CONFIGURATION_ITEM_MAP.get(itemId);
      if (!item) {
        errors.push(`Unknown configuration item '${itemId}'.`);
        return;
      }

      const rawState = draftEntries[itemId];
      if (!isPlainObject(rawState)) {
        errors.push(`${item.title}: configuration state must be an object.`);
        return;
      }

      const fallbackState = fallbackDraft[itemId] || {};
      const visible = rawState.visible !== undefined ? rawState.visible : rawState.Visible;
      const editable = rawState.editable !== undefined ? rawState.editable : rawState.Editable;
      const defaultEnabled = rawState.defaultEnabled !== undefined
        ? rawState.defaultEnabled
        : (rawState.DefaultEnabled !== undefined ? rawState.DefaultEnabled : (rawState.defaultValue !== undefined || rawState.DefaultValue !== undefined));
      const fixedEnabled = rawState.fixedEnabled !== undefined
        ? rawState.fixedEnabled
        : (rawState.FixedEnabled !== undefined ? rawState.FixedEnabled : (rawState.fixedValue !== undefined || rawState.FixedValue !== undefined));
      const defaultValue = rawState.defaultValue !== undefined ? rawState.defaultValue : rawState.DefaultValue;
      const fixedValue = rawState.fixedValue !== undefined ? rawState.fixedValue : rawState.FixedValue;

      if (visible !== undefined && typeof visible !== 'boolean') {
        errors.push(`${item.title}: visible must be true or false.`);
      }

      if (editable !== undefined && typeof editable !== 'boolean') {
        errors.push(`${item.title}: editable must be true or false.`);
      }

      if (defaultEnabled !== undefined && typeof defaultEnabled !== 'boolean') {
        errors.push(`${item.title}: defaultEnabled must be true or false.`);
      }

      if (fixedEnabled !== undefined && typeof fixedEnabled !== 'boolean') {
        errors.push(`${item.title}: fixedEnabled must be true or false.`);
      }

      const defaultValueError = validateConfigurationValue(item, defaultValue, 'defaultValue');
      if (defaultValueError) {
        errors.push(defaultValueError);
      }

      const fixedValueError = validateConfigurationValue(item, fixedValue, 'fixedValue');
      if (fixedValueError) {
        errors.push(fixedValueError);
      }

      nextDraft[itemId] = {
        visible: visible !== undefined ? visible : fallbackState.visible !== false,
        editable: editable !== undefined ? editable : fallbackState.editable !== false,
        defaultEnabled: defaultEnabled !== undefined ? defaultEnabled : Boolean(fallbackState.defaultEnabled),
        defaultValue: defaultValue !== undefined ? coerceConfigurationValue(item, defaultValue) : fallbackState.defaultValue,
        fixedEnabled: fixedEnabled !== undefined ? fixedEnabled : Boolean(fallbackState.fixedEnabled),
        fixedValue: fixedValue !== undefined ? coerceConfigurationValue(item, fixedValue) : fallbackState.fixedValue,
      };
    });
  }

  if (source.kind === 'raw' || source.kind === 'current') {
    nextDraft = createTrainingProfileConfigurationDraft({
      uiConfiguration: source.value.uiConfiguration,
      simulationDefaults: source.value.simulationDefaults,
      fixedValues: source.value.fixedValues,
    });
  }

  return {
    errors,
    provided: source.provided,
    draft: nextDraft,
    uiConfiguration: buildTrainingProfileUiConfiguration(nextDraft),
    simulationDefaults: buildTrainingProfileSimulationDefaults(nextDraft),
    fixedValues: buildTrainingProfileFixedValues(nextDraft),
  };
};

const resolveCountry = (rootPayload, countries, profile, errors) => {
  const rawCountryId = normalizeOptionalInteger(getFirstDefined(rootPayload, ['CountryId', 'countryId']));
  const rawCountryName = normalizeOptionalString(getFirstDefined(rootPayload, ['CountryName', 'countryName']));
  const rawCountryLanguage = normalizeOptionalString(getFirstDefined(rootPayload, ['CountryLanguage', 'countryLanguage']));
  const rawCountry = getFirstDefined(rootPayload, ['Country', 'country']);
  const providedCountry = hasAnyOwnKey(rootPayload, ['CountryId', 'countryId', 'CountryName', 'countryName', 'CountryLanguage', 'countryLanguage', 'Country', 'country']);

  let resolvedCountry = null;
  if (rawCountryId !== undefined) {
    resolvedCountry = countries.find((country) => country.id === rawCountryId) || null;
  }

  if (!resolvedCountry && isPlainObject(rawCountry)) {
    const nestedCountryId = normalizeOptionalInteger(getFirstDefined(rawCountry, ['CountryId', 'countryId', 'Id', 'id']));
    const nestedLanguage = normalizeOptionalString(getFirstDefined(rawCountry, ['CountryLanguage', 'countryLanguage', 'Language', 'language']));
    const nestedName = normalizeOptionalString(getFirstDefined(rawCountry, ['CountryName', 'countryName', 'Name', 'name']));
    resolvedCountry = countries.find((country) => (
      (nestedCountryId !== undefined && country.id === nestedCountryId)
      || (nestedLanguage && country.language?.toLowerCase() === nestedLanguage.toLowerCase())
      || (nestedName && country.name?.toLowerCase() === nestedName.toLowerCase())
    )) || null;
  }

  if (!resolvedCountry && (rawCountryLanguage || rawCountryName)) {
    resolvedCountry = countries.find((country) => (
      (rawCountryLanguage && country.language?.toLowerCase() === rawCountryLanguage.toLowerCase())
      || (rawCountryName && country.name?.toLowerCase() === rawCountryName.toLowerCase())
    )) || null;
  }

  if (!resolvedCountry && profile?.countryId) {
    resolvedCountry = countries.find((country) => country.id === profile.countryId) || null;
  }

  if (!resolvedCountry && providedCountry) {
    errors.push('Country could not be resolved from the provided CountryId, CountryName, or CountryLanguage.');
  }

  return resolvedCountry;
};

const normalizeQuizAssignments = (rootPayload, quizCollections, profile, errors) => {
  const rawAssignments = getFirstDefined(rootPayload, ['QuizAssignments', 'quizAssignments']);
  const sourceAssignments = Array.isArray(rawAssignments) ? rawAssignments : (rawAssignments === undefined ? (profile?.quizAssignments || []) : []);
  const provided = rawAssignments !== undefined;

  const normalizedAssignments = sourceAssignments.map((assignment, index) => {
    if (!isPlainObject(assignment)) {
      errors.push(`Quiz assignment ${index + 1} must be an object.`);
      return null;
    }

    const quizCollectionId = normalizeOptionalInteger(getFirstDefined(assignment, ['QuizCollectionId', 'quizCollectionId']));
    const quizCollectionName = normalizeOptionalString(getFirstDefined(assignment, ['QuizCollectionName', 'quizCollectionName', 'Name', 'name']));
    const quizCollection = quizCollectionId !== undefined
      ? quizCollections.find((entry) => entry.id === quizCollectionId)
      : quizCollections.find((entry) => quizCollectionName && entry.name?.toLowerCase() === quizCollectionName.toLowerCase());

    if (!quizCollection) {
      errors.push(`Quiz assignment ${index + 1} could not resolve a quiz collection.`);
      return null;
    }

    return {
      quizCollectionId: quizCollection.id,
      displayLabel: normalizeOptionalString(getFirstDefined(assignment, ['DisplayLabel', 'displayLabel'])) || null,
      orderIndex: normalizeOptionalInteger(getFirstDefined(assignment, ['OrderIndex', 'orderIndex'])) ?? index,
      quizCollectionName: quizCollection.name,
      publishedQuestionCount: quizCollection.publishedQuestionCount ?? quizCollection.questionCount ?? 0,
    };
  }).filter(Boolean);

  const requestedIds = normalizedAssignments.map((assignment) => assignment.quizCollectionId);
  if (requestedIds.length !== new Set(requestedIds).size) {
    errors.push('Each quiz collection can only be assigned once.');
  }

  return {
    provided,
    normalizedAssignments,
    requestAssignments: normalizedAssignments
      .sort((left, right) => left.orderIndex - right.orderIndex)
      .map((assignment, index) => ({
        quizCollectionId: assignment.quizCollectionId,
        displayLabel: assignment.displayLabel,
        orderIndex: index,
      })),
  };
};

const buildQuestValidationResults = (quests) => quests.map((quest, index) => {
  const errors = validateQuest(quest);
  const iconLabel = quest.iconKey ? (ICON_LABEL_MAP.get(quest.iconKey) || quest.iconKey) : null;

  return {
    index,
    title: quest.title || 'Untitled quest',
    category: quest.category || null,
    difficulty: quest.difficulty || null,
    estimatedTime: quest.estimatedTime || null,
    iconKey: quest.iconKey || null,
    iconLabel,
    iconKnown: quest.iconKey ? KNOWN_ICON_KEYS.has(quest.iconKey) : true,
    isChallenge: quest.isChallenge,
    stepCount: quest.steps.length,
    spotlightCount: quest.steps.filter((step) => step.spotlightTarget).length,
    answerCheckCount: quest.steps.reduce((total, step) => total + (Array.isArray(step.answerChecks) ? step.answerChecks.length : 0), 0),
    conditionUsed: Boolean(quest.conditionAst),
    stepTitles: quest.steps.slice(0, 4).map((step) => step.taskTitle || 'Untitled step'),
    errors,
    valid: errors.length === 0,
  };
});

const buildProfilePreview = ({
  rootPayload,
  profile,
  resolvedCountry,
  configurationDraft,
  normalizedAssignments,
  normalizedQuests,
  configurationProvided,
  assignmentsProvided,
  questsProvided,
}) => {
  const populatedConfigurationCount = Object.values(configurationDraft).filter((entry) => entry.defaultEnabled || entry.fixedEnabled || entry.visible === false || entry.editable === false).length;
  const spotlightCount = normalizedQuests.reduce((total, quest) => total + quest.steps.filter((step) => step.spotlightTarget).length, 0);
  const answerCheckCount = normalizedQuests.reduce((total, quest) => total + quest.steps.reduce((stepTotal, step) => stepTotal + (Array.isArray(step.answerChecks) ? step.answerChecks.length : 0), 0), 0);
  const conditionCount = normalizedQuests.filter((quest) => quest.conditionAst).length;
  const topLevelName = normalizeOptionalString(getFirstDefined(rootPayload, ['Name', 'name'])) || profile?.name || '';
  const additionalText = getFirstDefined(rootPayload, ['AdditionalTextRichContent', 'additionalTextRichContent']);
  const informationText = getFirstDefined(rootPayload, ['InformationRichContent', 'informationRichContent']);

  return {
    name: topLevelName,
    countryLabel: resolvedCountry ? `${resolvedCountry.language}${resolvedCountry.name ? ` · ${resolvedCountry.name}` : ''}` : 'Unresolved country',
    configurationItemCount: CONFIGURATION_ITEMS.length,
    populatedConfigurationCount,
    quizAssignmentCount: normalizedAssignments.length,
    questCount: normalizedQuests.length,
    stepCount: normalizedQuests.reduce((total, quest) => total + quest.steps.length, 0),
    spotlightCount,
    answerCheckCount,
    conditionCount,
    additionalTextPreview: stripHtml(additionalText !== undefined ? additionalText : profile?.additionalTextRichContent).slice(0, 180),
    informationTextPreview: stripHtml(informationText !== undefined ? informationText : profile?.informationRichContent).slice(0, 180),
    learningResourceCount: Array.isArray(profile?.learningResources) ? profile.learningResources.length : 0,
    sectionsProvided: {
      configuration: configurationProvided,
      quizAssignments: assignmentsProvided,
      tutorialQuests: questsProvided,
    },
  };
};

const getImportRootPayload = (parsedValue) => {
  if (Array.isArray(parsedValue)) {
    return { TutorialQuests: parsedValue };
  }

  if (!isPlainObject(parsedValue)) {
    return null;
  }

  return getFirstDefined(parsedValue, ['Profile', 'profile']) && isPlainObject(getFirstDefined(parsedValue, ['Profile', 'profile']))
    ? getFirstDefined(parsedValue, ['Profile', 'profile'])
    : parsedValue;
};

export const parseTrainingProfileImportText = (text, context = {}) => {
  if (!text || !String(text).trim()) {
    return {
      status: 'empty',
      isValid: false,
      error: null,
      request: null,
      profileErrors: [],
      questValidationResults: [],
      preview: null,
    };
  }

  let parsedValue;
  try {
    parsedValue = JSON.parse(text);
  } catch (error) {
    return {
      status: 'invalid-json',
      isValid: false,
      error: error.message,
      request: null,
      profileErrors: [],
      questValidationResults: [],
      preview: null,
    };
  }

  const rootPayload = getImportRootPayload(parsedValue);
  if (!rootPayload) {
    return {
      status: 'invalid-root',
      isValid: false,
      error: 'The import must be either a profile object or a tutorial quest array.',
      request: null,
      profileErrors: [],
      questValidationResults: [],
      preview: null,
    };
  }

  const { profile = null, countries = [], quizCollections = [] } = context;
  const profileErrors = [];

  const resolvedCountry = resolveCountry(rootPayload, countries, profile, profileErrors);
  const configuration = normalizeConfigurationDraft(rootPayload, profile);
  profileErrors.push(...configuration.errors);

  const quizAssignments = normalizeQuizAssignments(rootPayload, quizCollections, profile, profileErrors);

  const rawQuests = getFirstDefined(rootPayload, ['TutorialQuests', 'tutorialQuests', 'Quests', 'quests']);
  const questSource = Array.isArray(rawQuests)
    ? rawQuests
    : (rawQuests === undefined ? (Array.isArray(profile?.tutorialQuests) ? profile.tutorialQuests : []) : []);
  const normalizedQuests = questSource.map(normalizeQuest);
  const questValidationResults = buildQuestValidationResults(normalizedQuests);
  const questErrors = questValidationResults.flatMap((result) => result.errors);

  const topLevelName = normalizeOptionalString(getFirstDefined(rootPayload, ['Name', 'name'])) || profile?.name;
  if (!topLevelName) {
    profileErrors.push('Name is required.');
  }

  if (!resolvedCountry?.id) {
    profileErrors.push('CountryId is required.');
  }

  const request = topLevelName && resolvedCountry?.id
    ? {
        name: topLevelName,
        countryId: resolvedCountry.id,
        additionalTextRichContent: (() => {
          const rawAdditionalText = getFirstDefined(rootPayload, ['AdditionalTextRichContent', 'additionalTextRichContent']);
          if (rawAdditionalText !== undefined) {
            return mapNullableString(rawAdditionalText);
          }
          return mapNullableString(profile?.additionalTextRichContent);
        })(),
        informationRichContent: (() => {
          const rawInformation = getFirstDefined(rootPayload, ['InformationRichContent', 'informationRichContent']);
          if (rawInformation !== undefined) {
            return mapNullableString(rawInformation);
          }
          return mapNullableString(profile?.informationRichContent);
        })(),
        uiConfiguration: configuration.uiConfiguration,
        simulationDefaults: configuration.simulationDefaults,
        fixedValues: configuration.fixedValues,
        quizAssignments: quizAssignments.requestAssignments,
        tutorialQuests: normalizedQuests.map((quest, index) => mapQuestForRequest(quest, index)),
      }
    : null;

  const preview = buildProfilePreview({
    rootPayload,
    profile,
    resolvedCountry,
    configurationDraft: configuration.draft,
    normalizedAssignments: quizAssignments.normalizedAssignments,
    normalizedQuests,
    configurationProvided: configuration.provided,
    assignmentsProvided: quizAssignments.provided,
    questsProvided: rawQuests !== undefined,
  });

  return {
    status: profileErrors.length === 0 && questErrors.length === 0 ? 'valid' : 'invalid',
    isValid: profileErrors.length === 0 && questErrors.length === 0,
    error: null,
    parsedValue,
    request,
    profileErrors,
    questValidationResults,
    preview,
    normalizedQuests,
    configurationDraft: configuration.draft,
    assignments: quizAssignments.normalizedAssignments,
  };
};

export const buildTrainingProfileAiContext = ({ profile = null, countries = [], quizCollections = [] } = {}) => {
  const currentConfigurationDraft = createTrainingProfileConfigurationDraft({
    uiConfiguration: profile?.uiConfiguration,
    simulationDefaults: profile?.simulationDefaults,
    fixedValues: profile?.fixedValues,
  });

  return {
    ImportShape: {
      ExactPropertyNameRule: 'Use the PascalCase property names exactly as documented below. Do not rename keys, change case, translate property names, or invent new names.',
      TopLevelProperties: TOP_LEVEL_JSON_PROPERTIES,
      QuizAssignmentProperties: QUIZ_ASSIGNMENT_JSON_PROPERTIES,
      QuestProperties: QUEST_JSON_PROPERTIES,
      StepProperties: STEP_JSON_PROPERTIES,
      AnswerCheckProperties: ANSWER_CHECK_JSON_PROPERTIES,
      SpotlightTargetProperties: SPOTLIGHT_TARGET_JSON_PROPERTIES,
      ConditionAstProperties: CONDITION_AST_JSON_PROPERTIES,
      ConditionRuleProperties: CONDITION_RULE_JSON_PROPERTIES,
      Notes: [
        'If TutorialQuests, ConfigurationDraft, or QuizAssignments are omitted, the editor import flow keeps the current profile state for those sections before submitting the final payload.',
        'The current training language/country is kept from the existing profile unless you explicitly provide a different CountryId.',
        'Learning resource files, embedded rich-text image uploads, publication, and ownership are managed separately and are not imported from JSON.',
        'If you intentionally provide an empty TutorialQuests array or empty QuizAssignments array, that clears the existing section in the final import request.',
      ],
    },
    JsonPropertyGuideText: {
      TopLevel: buildPropertyGuideText('Top-level JSON properties', TOP_LEVEL_JSON_PROPERTIES),
      QuizAssignments: buildPropertyGuideText('Quiz assignment properties', QUIZ_ASSIGNMENT_JSON_PROPERTIES),
      TutorialQuests: buildPropertyGuideText('Tutorial quest properties', QUEST_JSON_PROPERTIES),
      Steps: buildPropertyGuideText('Tutorial step properties', STEP_JSON_PROPERTIES),
      AnswerChecks: buildPropertyGuideText('Answer check properties', ANSWER_CHECK_JSON_PROPERTIES),
      SpotlightTarget: buildPropertyGuideText('SpotlightTarget properties', SPOTLIGHT_TARGET_JSON_PROPERTIES),
      ConditionAst: buildPropertyGuideText('ConditionAst properties', CONDITION_AST_JSON_PROPERTIES),
      ConditionRule: buildPropertyGuideText('Condition rule properties', CONDITION_RULE_JSON_PROPERTIES),
    },
    HeatPumpSimulationToolOverview: buildSimulationToolOverview(),
    SimulationUiComponentReference: buildSimulationUiComponentReference(),
    QuestModalFeatureReference: buildQuestModalFeatureReference(),
    SpotlightAuthoringGuidance: buildSpotlightAuthoringGuidance(),
    AnswerCheckDecisionGuide: buildAnswerCheckDecisionGuide(),
    AuthoringIdeas: buildAuthoringIdeas(),
    ConfigurationDraftFieldSemantics: [
      'Each ConfigurationDraft entry is keyed by the configuration item id, for example outdoorTemperature or radiatorType.',
      'visible controls whether the learner can see the control or tool.',
      'editable controls whether the learner can change the control when it is visible.',
      'defaultEnabled and defaultValue seed the initial simulation state when the profile loads.',
      'fixedEnabled and fixedValue lock a value for the learner when that item supports fixed values.',
      'Use item ids from ConfigurationCatalog or ConfigurationCatalogText, not controlKey names, as the object keys in ConfigurationDraft.',
    ],
    ConfigurationCatalog: buildConfigurationCatalog(),
    ConfigurationCatalogText: buildConfigurationCatalogText(),
    ConditionAst: {
      combinators: CONDITION_COMBINATOR_DEFINITIONS,
      operators: CONDITION_OPERATOR_DEFINITIONS,
      fields: CONDITION_FIELD_DEFINITIONS,
      examples: {
        starter: DEFAULT_CONDITION,
        twoCondition: TWO_CONDITION_EXAMPLE,
        crossField: CROSS_FIELD_CONDITION,
      },
    },
    Spotlight: {
      positions: SPOTLIGHT_POSITION_DEFINITIONS,
      selectors: AVAILABLE_SPOTLIGHT_SELECTORS,
      selectorUsage: [
        'Use only selectors from the provided selector library unless you have verified a stable simulator DOM target.',
        'When selector matching could be fragile, include fallbackRect with x, y, width, and height.',
      ],
      expectedStepFields: ['JustInTimeRichContent', 'AnswerChecks', 'LockNextStepsUntilCorrect'],
      expectedStepRules: [
        'Each AnswerChecks item must be either an expected-value pair (Name + Value), a yes/no question (Question + Correct), or a multiple-choice question (Question + Options + CorrectOptionIndexes).',
        'Step-level JustInTimeRichContent and answer-check-level JustInTimeRichContent are optional rich HTML help panels shown only when the learner clicks the help icon.',
        'Expected-value checks must store Value as a JSON string, not a JSON number. Example: "35", not 35.',
        'LockNextStepsUntilCorrect may only be true when at least one AnswerChecks item is configured.',
      ],
      notes: [
        'SpotlightTarget supports selector, position, optional displayName, optional fallbackRect, and legacy fallback.',
        'Use only selectors from the provided library unless you have verified a stable simulator DOM target.',
      ],
    },
    KnownQuestIcons: AVAILABLE_QUEST_ICONS.map((icon) => ({
      key: icon.key,
      label: icon.label,
    })),
    AvailableCountries: countries.map((country) => ({
      Id: country.id,
      Name: country.name,
      Language: country.language,
    })),
    AvailableQuizCollections: quizCollections.map((quizCollection) => ({
      QuizCollectionId: quizCollection.id,
      Name: quizCollection.name,
      PublishedQuestionCount: quizCollection.publishedQuestionCount ?? quizCollection.questionCount ?? 0,
    })),
    CurrentProfileSnapshot: profile ? {
      Id: profile.id,
      Name: profile.name,
      CountryId: profile.countryId,
      CountryName: profile.countryName,
      CountryLanguage: profile.countryLanguage,
      IsPublished: profile.isPublished,
      AdditionalTextPreview: stripHtml(profile.additionalTextRichContent).slice(0, 240),
      InformationTextPreview: stripHtml(profile.informationRichContent).slice(0, 240),
      CurrentConfigurationDraft: serializeConfigurationDraft(currentConfigurationDraft),
      CurrentTutorialQuests: (profile.tutorialQuests || []).map((quest) => ({
        Title: quest.title,
        StepCount: Array.isArray(quest.steps) ? quest.steps.length : 0,
        IsChallenge: Boolean(quest.isChallenge),
        HasConditionAst: Boolean(quest.conditionAst),
      })),
      CurrentQuizAssignments: (profile.quizAssignments || []).map((assignment) => ({
        QuizCollectionId: assignment.quizCollectionId,
        QuizCollectionName: assignment.quizCollectionName,
        DisplayLabel: assignment.displayLabel,
        OrderIndex: assignment.orderIndex,
      })),
      LearningResources: (profile.learningResources || []).map((resource) => ({
        FileName: resource.fileName,
        FileExtension: resource.fileExtension,
        FileSizeBytes: resource.fileSizeBytes,
      })),
    } : null,
  };
};

export const buildTrainingProfileAiInstructions = () => [
  'Generate one valid JSON object for the HeatCraftHP heat-pump training-profile import tool.',
  'Return JSON only. Do not return markdown, explanations, comments, code fences, or extra text.',
  'Use the PascalCase JSON property names exactly as documented in the AI reference JSON. Do not rename keys, do not change case, and do not invent alternative property names.',
  'Prefer ConfigurationDraft over raw UiConfiguration, SimulationDefaults, and FixedValues because ConfigurationDraft is easier to author correctly and is fully supported by the import parser.',
  'Write rich-content fields as concise HTML strings such as <p>...</p>, <ul><li>...</li></ul>, or short multi-paragraph HTML blocks.',
  'The simulation tool includes outdoor-climate controls, building-envelope controls, radiator selection, room and supply settings, thermostat and valve behaviour, overlays, COP and temperature readouts, toolbar actions, learning resources, and guided tutorial quests.',
  'Use SimulationUiComponentReference from the AI reference JSON to describe components such as the thermostat area, preset valve, radiator section, COP display, supply temperature control, and outdoor temperature input in a technically balanced way.',
  'The quest modal can show learning objectives, scenario, collapsible steps, completion conditions, knowledge synthesis, completion banner state, reset action, and Start Journey spotlight guidance.',
  'Use TutorialQuests to describe scenario-driven learning journeys. Each quest can include category, difficulty, estimated time, icon, challenge flag, six rich-text content sections, an optional ConditionAst, and nested Steps.',
  'Every step can include TaskTitle, instructional rich text, detailed guidance rich text, success-indicator rich text, optional JustInTimeRichContent, optional ordered AnswerChecks, and an optional SpotlightTarget.',
  'If you add SpotlightTarget, also write step text that explicitly references the highlighted element. Use SpotlightAuthoringGuidance for short DisplayName suggestions and example step copy.',
  'Each AnswerChecks item must be either an expected-value pair (Name + Value), a yes/no question (Question + Correct), or a multiple-choice question (Question + Options + CorrectOptionIndexes). Do not mix shapes in one item. Each answer check may also include optional JustInTimeRichContent shown behind a help icon.',
  'Expected-value check Value must be a JSON string, not a JSON number. Example: "35", not 35.',
  'Use AnswerChecks only for learner responses entered inside the quest step UI. Do not use AnswerChecks for values the learner sets or reads directly in simulator controls or simulator readouts.',
  'Use JustInTimeRichContent for brief coaching, hints, or reference explanations that should stay hidden until requested. Keep it concise and specific to the step or answer check.',
  'If the learner must reach or verify simulator state such as outdoorTemperature = -7, selectedCity = Aachen, supplyTemperature >= 45, or COP above a threshold, use ConditionAst. If the scenario should start with those values, use ConfigurationDraft defaults or fixed values.',
  'Set LockNextStepsUntilCorrect to true only when at least one AnswerChecks item is present.',
  'Use only ConditionAst fields and operators from the AI reference JSON. Use only spotlight selectors from the provided selector library.',
  'The AI reference JSON includes ConfigurationCatalogText, structured property guides, SimulationUiComponentReference, SpotlightAuthoringGuidance, and AnswerCheckDecisionGuide. Follow those sections closely.',
  'When importing into an existing profile, omitted sections are filled from the current profile snapshot before the final request is sent. If you intentionally send an empty TutorialQuests array or QuizAssignments array, that clears that section.',
  'Do not invent learning-resource uploads, ownership changes, publication changes, or unsupported simulator fields inside the JSON payload.',
].join('\n');

export const generateTrainingProfileImportTemplate = ({ profile = null, countries = [], quizCollections = [] } = {}) => {
  const templateConfigurationDraft = buildTemplateConfigurationDraft(profile);
  const spotlightTargets = AVAILABLE_SPOTLIGHT_SELECTORS.slice(0, 7);
  const resolvedCountryId = profile?.countryId ?? countries[0]?.id ?? null;
  const templateQuizAssignments = Array.isArray(profile?.quizAssignments) && profile.quizAssignments.length > 0
    ? profile.quizAssignments.map((assignment, index) => ({
        QuizCollectionId: assignment.quizCollectionId,
        OrderIndex: index,
        DisplayLabel: assignment.displayLabel || assignment.quizCollectionName || null,
      }))
    : quizCollections.slice(0, 2).map((quizCollection, index) => ({
        QuizCollectionId: quizCollection.id,
        OrderIndex: index,
        DisplayLabel: index === 0 ? 'Core skill check' : 'Stretch skill check',
      }));

  const template = {
    Name: profile?.name || 'Cold-weather commissioning and optimization lab',
    CountryId: resolvedCountryId,
    AdditionalTextRichContent: profile?.additionalTextRichContent || '<p>This profile guides learners through startup, diagnostic reasoning, and optimization decisions inside the heat-pump simulation.</p><p>It combines guided journeys, condition-based quest completion, and optional skill-quiz follow-up.</p>',
    InformationRichContent: profile?.informationRichContent || '<p><strong>Start here.</strong> Use this training profile to orient the learner before they interact with the simulator controls.</p><p>Review the learning goal, point out the most important system cues, and explain how the quests will build from guided observation into reasoned intervention.</p>',
    QuizAssignments: templateQuizAssignments,
    ConfigurationDraft: templateConfigurationDraft,
    TutorialQuests: [
      {
        OrderIndex: 0,
        Title: 'Cold-weather startup walkthrough',
        Category: 'Commissioning',
        Difficulty: 'Beginner',
        EstimatedTime: '8 minutes',
        IconKey: AVAILABLE_QUEST_ICONS[0]?.key || 'aim',
        IsChallenge: false,
        LearningObjectivesRichContent: '<p>Identify the startup controls, locate the first KPIs, and explain why the learner should verify system state before making adjustments.</p>',
        ScenarioRichContent: '<p>A junior installer is preparing a heat-pump training rig for its first cold-weather startup after basic commissioning.</p>',
        RealWorldConnectionRichContent: '<p>This mirrors the first supervised handover on a real project where the trainer must connect interface actions to system behavior.</p>',
        CompletionDescriptionRichContent: '<p>Complete the startup checks and confirm that the simulation is actively updating live values.</p>',
        KnowledgeSynthesisRichContent: '<p>Startup is not only about turning the system on. It is about confirming that the observed readings match the expected operating state.</p>',
        PracticalTipRichContent: '<p>Name the metric to watch before the learner changes anything. That reduces guessing and improves reasoning.</p>',
        ConditionAst: DEFAULT_CONDITION,
        Steps: [
          {
            OrderIndex: 0,
            StepKey: 'startup-1',
            TaskTitle: 'Start the simulation',
            InstructionRichContent: '<p>Use the run button in the toolbar to begin the simulation.</p>',
            DetailedGuidanceRichContent: '<p>Explain that no live KPI interpretation is meaningful until the simulation is actually running.</p>',
            SuccessIndicatorRichContent: '<p>The system begins updating live values and the simulation is clearly active.</p>',
            JustInTimeRichContent: '<p>If the learner is unsure whether the simulation really started, point them to any value that continuously updates while the run mode is active.</p>',
            AnswerChecks: [],
            LockNextStepsUntilCorrect: false,
            SpotlightTarget: {
              selector: spotlightTargets[0]?.selector,
              position: 'bottom',
              displayName: spotlightTargets[0]?.name || 'Run animation button',
              fallbackRect: { x: 80, y: 120, width: 44, height: 44 },
            },
          },
          {
            OrderIndex: 1,
            StepKey: 'startup-2',
            TaskTitle: 'Read the COP display',
            InstructionRichContent: '<p>Locate the COP display and say what it represents before reading the current value.</p>',
            DetailedGuidanceRichContent: '<p>Use this step to connect efficiency language with the learner-facing dashboard instead of treating COP as an isolated number.</p>',
            SuccessIndicatorRichContent: '<p>The learner can point to the COP display and explain why it matters.</p>',
            AnswerChecks: [],
            LockNextStepsUntilCorrect: false,
            SpotlightTarget: {
              selector: spotlightTargets[1]?.selector,
              position: 'right',
              displayName: spotlightTargets[1]?.name || 'COP display',
            },
          },
          {
            OrderIndex: 2,
            StepKey: 'startup-3',
            TaskTitle: 'Check the outdoor temperature context',
            InstructionRichContent: '<p>Find the outdoor temperature and explain why it frames the rest of the startup decisions.</p>',
            DetailedGuidanceRichContent: '<p>Cold-weather context should shape how the learner interprets supply temperature and heat demand.</p>',
            SuccessIndicatorRichContent: '<p>The learner links the displayed outdoor condition to later control choices.</p>',
            AnswerChecks: [],
            LockNextStepsUntilCorrect: false,
            SpotlightTarget: {
              selector: spotlightTargets[2]?.selector,
              position: 'left',
              displayName: spotlightTargets[2]?.name || 'Outdoor temperature',
            },
          },
        ],
      },
      {
        OrderIndex: 1,
        Title: 'Envelope and ventilation diagnosis',
        Category: 'Diagnostics',
        Difficulty: 'Intermediate',
        EstimatedTime: '10 minutes',
        IconKey: AVAILABLE_QUEST_ICONS[1]?.key || 'tool',
        IsChallenge: false,
        LearningObjectivesRichContent: '<p>Relate building envelope assumptions and ventilation rate to the heat-loss picture inside the simulator.</p>',
        ScenarioRichContent: '<p>The learner must decide whether poor thermal performance is driven more by envelope quality or ventilation behavior.</p>',
        RealWorldConnectionRichContent: '<p>This reflects retrofit conversations where installers and consultants need to justify whether control changes alone will solve the comfort complaint.</p>',
        CompletionDescriptionRichContent: '<p>Confirm a plausible diagnostic path using building inputs and observed losses.</p>',
        KnowledgeSynthesisRichContent: '<p>Comfort problems often involve both system settings and building characteristics. Good training profiles force learners to reason across both.</p>',
        PracticalTipRichContent: '<p>Ask the learner to say which metric they expect to change before they touch the next control.</p>',
        ConditionAst: TWO_CONDITION_EXAMPLE,
        Steps: [
          {
            OrderIndex: 0,
            StepKey: 'diagnostic-1',
            TaskTitle: 'Inspect the building controls',
            InstructionRichContent: '<p>Open the building section and identify the inputs that influence envelope-related heat loss.</p>',
            DetailedGuidanceRichContent: '<p>Prompt the learner to connect U-value, total area, and ventilation assumptions with the displayed losses.</p>',
            SuccessIndicatorRichContent: '<p>The learner can explain which building-side settings affect heat loss.</p>',
            AnswerChecks: [],
            LockNextStepsUntilCorrect: false,
            SpotlightTarget: {
              selector: spotlightTargets[3]?.selector,
              position: 'left',
              displayName: spotlightTargets[3]?.name || 'Building section',
            },
          },
          {
            OrderIndex: 1,
            StepKey: 'diagnostic-2',
            TaskTitle: 'Use the ventilation cue',
            InstructionRichContent: '<p>Check the ventilation-related control or display and decide whether air exchange is part of the issue.</p>',
            DetailedGuidanceRichContent: '<p>Keep the learner focused on reasoning from what is visible in the simulator instead of making unsupported assumptions.</p>',
            SuccessIndicatorRichContent: '<p>The learner can describe how ventilation affects the total loss picture.</p>',
            AnswerChecks: [],
            LockNextStepsUntilCorrect: false,
            SpotlightTarget: {
              selector: spotlightTargets[4]?.selector,
              position: 'right',
              displayName: spotlightTargets[4]?.name || 'Ventilation element',
              fallbackRect: { x: 760, y: 430, width: 220, height: 70 },
            },
          },
        ],
      },
      {
        OrderIndex: 2,
        Title: 'Supply temperature optimization challenge',
        Category: 'Optimization',
        Difficulty: 'Advanced',
        EstimatedTime: '12 minutes',
        IconKey: AVAILABLE_QUEST_ICONS[2]?.key || 'calculator',
        IsChallenge: true,
        LearningObjectivesRichContent: '<p>Adjust supply temperature, interpret outdoor conditions, and justify an efficiency-oriented setting choice.</p>',
        ScenarioRichContent: '<p>The system is operating during a cold snap, and the learner must avoid the common mistake of using unnecessarily high supply temperatures.</p>',
        RealWorldConnectionRichContent: '<p>This mirrors service visits where comfort complaints tempt technicians to increase flow temperatures beyond what the building actually needs.</p>',
        CompletionDescriptionRichContent: '<p>Reach a stable recommendation and justify it with both measured context and learner reasoning.</p>',
        KnowledgeSynthesisRichContent: '<p>Higher supply temperature can quickly improve perceived comfort, but it often harms efficiency. The best choice balances demand and performance.</p>',
        PracticalTipRichContent: '<p>Use a challenge quest when you want the learner to commit to a recommendation rather than just explore controls.</p>',
        ConditionAst: CROSS_FIELD_CONDITION,
        Steps: [
          {
            OrderIndex: 0,
            StepKey: 'challenge-1',
            TaskTitle: 'Locate the supply temperature control',
            InstructionRichContent: '<p>Find the supply temperature control and explain why it is one of the main efficiency levers in the simulation.</p>',
            DetailedGuidanceRichContent: '<p>Connect control authority with what the learner expects to happen to COP and delivered heat.</p>',
            SuccessIndicatorRichContent: '<p>The learner points to the correct control and explains its importance.</p>',
            AnswerChecks: [],
            LockNextStepsUntilCorrect: false,
            SpotlightTarget: {
              selector: spotlightTargets[5]?.selector,
              position: 'left',
              displayName: spotlightTargets[5]?.name || 'Supply temperature control',
            },
          },
          {
            OrderIndex: 1,
            StepKey: 'challenge-2',
            TaskTitle: 'Use outdoor temperature to justify a recommendation',
            InstructionRichContent: '<p>Check the outdoor temperature and enter the recommended supply temperature as text.</p>',
            DetailedGuidanceRichContent: '<p>Expected-value answers are stored as text. Keep the learner focused on a reasoned recommendation, not blind trial and error.</p>',
            SuccessIndicatorRichContent: '<p>The learner provides a justified recommendation that fits the current conditions.</p>',
            JustInTimeRichContent: '<p>Use this only if the learner stalls: remind them to anchor the recommendation to both the cold-weather demand and the efficiency impact of higher flow temperatures.</p>',
            AnswerChecks: [
              {
                OrderIndex: 0,
                Name: 'Recommended supply temperature',
                Value: '35',
                JustInTimeRichContent: '<p>A mild-to-moderate recommended value can be defensible here because the learner is targeting efficiency, not maximum possible heat delivery.</p>',
              },
              {
                OrderIndex: 1,
                Question: 'Is lowering the supply temperature expected to improve COP in this scenario?',
                Correct: true,
                JustInTimeRichContent: '<p>Think about the usual relationship between lower supply temperature and compressor efficiency before answering.</p>',
              },
            ],
            LockNextStepsUntilCorrect: true,
            SpotlightTarget: {
              selector: spotlightTargets[6]?.selector || spotlightTargets[2]?.selector,
              position: 'left',
              displayName: spotlightTargets[6]?.name || 'Outdoor temperature display',
            },
          },
          {
            OrderIndex: 2,
            StepKey: 'challenge-3',
            TaskTitle: 'Summarize the tradeoff',
            InstructionRichContent: '<p>Summarize the balance between comfort, heat output, and COP after the adjustment.</p>',
            DetailedGuidanceRichContent: '<p>Use this final step to force synthesis: what changed, why it changed, and why the recommended setting is defensible.</p>',
            SuccessIndicatorRichContent: '<p>The learner can articulate the tradeoff clearly and connect the chosen setting to observed behavior.</p>',
            AnswerChecks: [],
            LockNextStepsUntilCorrect: false,
            SpotlightTarget: null,
          },
        ],
      },
    ],
  };

  return JSON.stringify(template, null, 2);
};

/**
 * Build the current training profile as an import-ready JSON document.
 * This gives authors a round-trippable payload they can send to external AI tools
 * and paste back into the whole-profile import editor after revision.
 *
 * @param {object} params Export parameters.
 * @param {object|null} params.profile Current training profile detail snapshot.
 * @returns {string} Pretty-printed import-ready JSON.
 */
export const generateTrainingProfileCurrentImportJson = ({ profile = null } = {}) => {
  const currentConfigurationDraft = serializeConfigurationDraft(createTrainingProfileConfigurationDraft({
    uiConfiguration: profile?.uiConfiguration,
    simulationDefaults: profile?.simulationDefaults,
    fixedValues: profile?.fixedValues,
  }));

  const currentProfilePayload = {
    Name: profile?.name || '',
    CountryId: profile?.countryId ?? null,
    AdditionalTextRichContent: mapNullableString(profile?.additionalTextRichContent),
    InformationRichContent: mapNullableString(profile?.informationRichContent),
    ConfigurationDraft: currentConfigurationDraft,
    QuizAssignments: Array.isArray(profile?.quizAssignments)
      ? profile.quizAssignments.map((assignment, index) => ({
          QuizCollectionId: assignment?.quizCollectionId,
          OrderIndex: Number.isInteger(assignment?.orderIndex) ? assignment.orderIndex : index,
          DisplayLabel: mapNullableString(assignment?.displayLabel),
        }))
      : [],
    TutorialQuests: Array.isArray(profile?.tutorialQuests)
      ? profile.tutorialQuests.map((quest, index) => mapQuestForExport(quest, index))
      : [],
  };

  return JSON.stringify(currentProfilePayload, null, 2);
};